import React, { useEffect, useRef, useState } from 'react';
import { IonIcon } from '@ionic/react';
import { searchOutline, closeOutline } from 'ionicons/icons';
import { getMembers } from '@/api/members';
import { MEMBERSHIP_TYPE_LABELS } from '@/utils/labels';
import type { MembershipType } from '@/types/member';

export interface MemberSuggestion {
  id: string;
  full_name: string;
  cell_phone?: string | null;
  membership_type?: string | null;
  birth_date?: string | null;
}

interface Props {
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onSelect?: (member: MemberSuggestion) => void;
  onSubmit?: (value: string) => void;
  /** Pre-loaded members for client-side filtering (communion). If omitted, uses API. */
  members?: MemberSuggestion[];
  className?: string;
  variant?: 'hero' | 'pill';
}

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[đ]/g, 'd');
}

const MemberSearchInput: React.FC<Props> = ({
  placeholder = 'Caută persoane...',
  value,
  onChange,
  onSelect,
  onSubmit,
  members,
  className,
  variant = 'pill',
}) => {
  const [suggestions, setSuggestions] = useState<MemberSuggestion[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    clearTimeout(timerRef.current);
    const q = value.trim();

    if (q.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    if (members) {
      const needle = normalize(q);
      const filtered = members
        .filter((m) => normalize(m.full_name).includes(needle))
        .slice(0, 6);
      setSuggestions(filtered);
      setShowDropdown(filtered.length > 0);
      setActiveIdx(-1);
      return;
    }

    timerRef.current = setTimeout(async () => {
      try {
        // `ordering` opts out of the roster's family grouping: a dropdown is
        // for finding one person, so the backend ranks by how well each row
        // matches and then sorts by name, rather than returning six members of
        // whichever family sorted first.
        const res = await getMembers({ search: q, page_size: 6, ordering: 'full_name' });
        setSuggestions(res.data);
        setShowDropdown(res.data.length > 0);
        setActiveIdx(-1);
      } catch { /* ignore */ }
    }, 300);

    return () => clearTimeout(timerRef.current);
  }, [value, members]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) {
      if (e.key === 'Enter' && onSubmit) { onSubmit(value); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIdx >= 0 && suggestions[activeIdx]) {
        handleSelect(suggestions[activeIdx]);
      } else if (onSubmit) {
        setShowDropdown(false);
        onSubmit(value);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleSelect = (member: MemberSuggestion) => {
    setShowDropdown(false);
    onSelect?.(member);
  };

  const isHero = variant === 'hero';

  return (
    <div ref={wrapperRef} className={`member-search-wrap ${className ?? ''}`}>
      <div className={isHero ? 'dash-search' : 'crm-search-pill'}>
        <IonIcon
          icon={searchOutline}
          className={isHero ? 'dash-search__icon' : 'crm-search-pill__icon'}
        />
        <input
          ref={inputRef}
          type="text"
          className={isHero ? 'dash-search__input' : 'crm-search-pill__input'}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
          aria-label={placeholder}
          autoComplete="off"
        />
        {value && !isHero && (
          <button
            className="crm-search-pill__clear"
            onClick={() => { onChange(''); setSuggestions([]); }}
            aria-label="Șterge căutare"
            type="button"
          >
            <IonIcon icon={closeOutline} />
          </button>
        )}
        {isHero && onSubmit && (
          <button
            className="dash-search__btn"
            onClick={() => { setShowDropdown(false); onSubmit(value); }}
            type="button"
          >
            <IonIcon icon={searchOutline} style={{ fontSize: 18 }} />
            Caută
          </button>
        )}
      </div>

      {showDropdown && suggestions.length > 0 && (
        <ul className="member-search-suggestions">
          {suggestions.map((m, i) => (
            <li
              key={m.id}
              className={`member-search-suggestions__item${i === activeIdx ? ' member-search-suggestions__item--active' : ''}`}
              onMouseDown={() => handleSelect(m)}
              onMouseEnter={() => setActiveIdx(i)}
            >
              <span className="member-search-suggestions__name">{m.full_name}</span>
              <span className="member-search-suggestions__meta">
                {m.membership_type ? MEMBERSHIP_TYPE_LABELS[m.membership_type as MembershipType] ?? m.membership_type : ''}
                {m.cell_phone ? ` · ${m.cell_phone}` : ''}
                {m.birth_date ? ` · ${m.birth_date}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MemberSearchInput;
